import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Settings, RefreshCw, CreditCard, Mail, Sparkles, FileText,
  AlertTriangle, Shield, Globe, Lock
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));
const API = `${BACKEND_URL}/api`;

export default function BackofficeSettings() {
  const { theme } = useOutletContext();
  const isDark = theme === 'dark';

  const [systemConfig, setSystemConfig] = useState(null);
  const [backofficeSettings, setBackofficeSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [testingConfig, setTestingConfig] = useState(false);

  // Dialogs
  const [showStripeConfig, setShowStripeConfig] = useState(false);
  const [showEmailConfig, setShowEmailConfig] = useState(false);
  const [showAiConfig, setShowAiConfig] = useState(false);

  // Forms
  const [stripeForm, setStripeForm] = useState({ provider: "stripe", api_key: "", publishable_key: "", webhook_secret: "" });
  const [emailForm, setEmailForm] = useState({ provider: "brevo", api_key: "", sender_email: "", sender_name: "" });
  const [aiForm, setAiForm] = useState({ provider: "openai", api_key: "", model: "gpt-4o-mini" });

  // IP Whitelist & Blocked
  const [ipWhitelist, setIpWhitelist] = useState([]);
  const [blockedIps, setBlockedIps] = useState([]);
  const [newIp, setNewIp] = useState({ ip: "", description: "" });
  const [newBlockedIp, setNewBlockedIp] = useState({ ip: "", reason: "" });
  const [loadingWhitelist, setLoadingWhitelist] = useState(false);

  useEffect(() => {
    fetchConfig();
    fetchBackofficeSettings();
    fetchWhitelist();
  }, []);
const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/config/all`, { withCredentials: true });
      setSystemConfig(res.data.config);
    } catch (error) {
      console.error("Failed to fetch config:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBackofficeSettings = async () => {
    try {
      const res = await axios.get(`${API}/backoffice/settings`, { withCredentials: true });
      setBackofficeSettings(res.data);
    } catch (error) {
      console.error("Failed to fetch backoffice settings:", error);
    }
  };

  const fetchWhitelist = async () => {
    setLoadingWhitelist(true);
    try {
      const res = await axios.get(`${API}/backoffice/whitelist`, { withCredentials: true });
      setIpWhitelist(res.data.whitelist || []);
      setBlockedIps(res.data.blocked_ips || []);
    } catch (error) {
      console.error("Failed to fetch whitelist:", error);
    } finally {
      setLoadingWhitelist(false);
    }
  };

  const handleSaveStripeConfig = async (e) => {
    e.preventDefault();
    if (!stripeForm.api_key || !stripeForm.publishable_key) {
      toast.error("API Key and Publishable Key are required");
      return;
    }
    setSavingConfig(true);
    try {
      await axios.post(`${API}/config/stripe`, stripeForm, { withCredentials: true });
      toast.success("Stripe configuration saved!");
      setShowStripeConfig(false);
      fetchConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save Stripe config");
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSaveEmailConfig = async (e) => {
    e.preventDefault();
    if (!emailForm.api_key || !emailForm.sender_email) {
      toast.error("API Key and Sender Email are required");
      return;
    }
    setSavingConfig(true);
    try {
      await axios.post(`${API}/config/email`, emailForm, { withCredentials: true });
      toast.success("Email configuration saved!");
      setShowEmailConfig(false);
      fetchConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save Email config");
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSaveAiConfig = async (e) => {
    e.preventDefault();
    if (!aiForm.api_key) {
      toast.error("API Key is required");
      return;
    }
    setSavingConfig(true);
    try {
      await axios.post(`${API}/config/ai`, aiForm, { withCredentials: true });
      toast.success("AI configuration saved!");
      setShowAiConfig(false);
      fetchConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save AI config");
    } finally {
      setSavingConfig(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingConfig(true);
    try {
      const res = await axios.get(`${API}/config/test/email`, { withCredentials: true });
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.error || "Email test failed");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to test email");
    } finally {
      setTestingConfig(false);
    }
  };

  const handleTestStripe = async () => {
    setTestingConfig(true);
    try {
      const res = await axios.get(`${API}/config/test/stripe`, { withCredentials: true });
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.error || "Stripe test failed");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to test Stripe");
    } finally {
      setTestingConfig(false);
    }
  };

  const addIpToWhitelist = async () => {
    if (!newIp.ip) {
      toast.error("IP address is required");
      return;
    }
    try {
      await axios.post(`${API}/backoffice/whitelist/add`, newIp, { withCredentials: true });
      toast.success("IP added to whitelist");
      setNewIp({ ip: "", description: "" });
      fetchWhitelist();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add IP");
    }
  };

  const removeIpFromWhitelist = async (ip) => {
    try {
      await axios.post(`${API}/backoffice/whitelist/remove`, { ip }, { withCredentials: true });
      toast.success("IP removed from whitelist");
      fetchWhitelist();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove IP");
    }
  };

  const addIpToBlocklist = async () => {
    if (!newBlockedIp.ip) {
      toast.error("IP address is required");
      return;
    }
    try {
      await axios.post(`${API}/backoffice/blocked-ips/add`, newBlockedIp, { withCredentials: true });
      toast.success("IP added to blocklist");
      setNewBlockedIp({ ip: "", reason: "" });
      fetchWhitelist();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to block IP");
    }
  };

  const removeIpFromBlocklist = async (ip) => {
    try {
      await axios.post(`${API}/backoffice/blocked-ips/remove`, { ip }, { withCredentials: true });
      toast.success("IP removed from blocklist");
      fetchWhitelist();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unblock IP");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Settings</h1>
          <p className={`mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Configure platform integrations and security</p>
        </div>
        <Button onClick={fetchConfig} variant="outline" size="sm" className="gap-2">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* System Configuration */}
      <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>System Configuration</h2>
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Manage API keys and third-party integrations</p>
          </div>
        </div>

        {loading && !systemConfig ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 text-gray-300 mx-auto animate-spin mb-2" />
            <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>Loading configuration...</p>
          </div>
        ) : (
          <div className="grid gap-6">
            {/* Stripe Configuration */}
            <div className={`border rounded-lg p-4 ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
              <div className="flex justify-between items-start">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-violet-100 rounded-lg">
                    <CreditCard className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Stripe (Payments)</h3>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Payment processing and subscriptions</p>
                    {systemConfig?.stripe && (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs">
                          <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>API Key:</span>{" "}
                          <code className={`px-1 rounded ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>{systemConfig.stripe.api_key}</code>
                        </p>
                        <p className="text-xs">
                          <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Publishable Key:</span>{" "}
                          <code className="text-green-600">{systemConfig.stripe.publishable_key?.substring(0, 20)}...</code>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    systemConfig?.stripe?.configured ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {systemConfig?.stripe?.configured ? 'Configured' : 'Not Configured'}
                  </span>
                  <Button size="sm" variant="outline" onClick={handleTestStripe} disabled={testingConfig}>
                    Test
                  </Button>
                  <Button size="sm" onClick={() => {
                    setStripeForm({ provider: "stripe", api_key: "", publishable_key: "", webhook_secret: "" });
                    setShowStripeConfig(true);
                  }}>
                    Edit
                  </Button>
                </div>
              </div>
            </div>

            {/* Email Configuration */}
            <div className={`border rounded-lg p-4 ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
              <div className="flex justify-between items-start">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Mail className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Email ({systemConfig?.email?.provider || 'Brevo'})</h3>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Transactional emails and notifications</p>
                    {systemConfig?.email && (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs">
                          <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>API Key:</span>{" "}
                          <code className={`px-1 rounded ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>{systemConfig.email.api_key}</code>
                        </p>
                        <p className="text-xs">
                          <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Sender:</span>{" "}
                          <span className="text-blue-600">{systemConfig.email.sender_email || 'Not set'}</span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    systemConfig?.email?.configured ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {systemConfig?.email?.configured ? 'Configured' : 'Not Configured'}
                  </span>
                  <Button size="sm" variant="outline" onClick={handleTestEmail} disabled={testingConfig}>
                    Test
                  </Button>
                  <Button size="sm" onClick={() => {
                    setEmailForm({ 
                      provider: systemConfig?.email?.provider || "brevo", 
                      api_key: "", 
                      sender_email: systemConfig?.email?.sender_email || "",
                      sender_name: systemConfig?.email?.sender_name || "SeatPilot"
                    });
                    setShowEmailConfig(true);
                  }}>
                    Edit
                  </Button>
                </div>
              </div>
            </div>

            {/* AI Configuration */}
            <div className={`border rounded-lg p-4 ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
              <div className="flex justify-between items-start">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>AI Chat ({systemConfig?.ai_chat?.provider || 'OpenAI'})</h3>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>AI-powered customer support chat</p>
                    {systemConfig?.ai_chat && (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs">
                          <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>API Key:</span>{" "}
                          <code className={`px-1 rounded ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>{systemConfig.ai_chat.api_key}</code>
                        </p>
                        <p className="text-xs">
                          <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Model:</span>{" "}
                          <span className="text-purple-600">{systemConfig.ai_chat.model || 'gpt-4o-mini'}</span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    systemConfig?.ai_chat?.configured ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {systemConfig?.ai_chat?.configured ? 'Configured' : 'Not Configured'}
                  </span>
                  <Button size="sm" onClick={() => {
                    setAiForm({ 
                      provider: systemConfig?.ai_chat?.provider || "openai", 
                      api_key: "", 
                      model: systemConfig?.ai_chat?.model || "gpt-4o-mini"
                    });
                    setShowAiConfig(true);
                  }}>
                    Edit
                  </Button>
                </div>
              </div>
            </div>

            {/* Database Info (Read Only) */}
            <div className={`border rounded-lg p-4 ${isDark ? 'border-slate-700 bg-slate-700/30' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex justify-between items-start">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${isDark ? 'bg-slate-600' : 'bg-gray-200'}`}>
                    <FileText className={`w-5 h-5 ${isDark ? 'text-slate-300' : 'text-gray-600'}`} />
                  </div>
                  <div>
                    <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Database (MongoDB)</h3>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Primary data storage</p>
                    {systemConfig?.database && (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs">
                          <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>URL:</span>{" "}
                          <code className={`px-1 rounded ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>{systemConfig.database.url}</code>
                        </p>
                        <p className="text-xs">
                          <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Database:</span>{" "}
                          <span className="text-green-600">{systemConfig.database.name}</span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  systemConfig?.database?.configured ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  {systemConfig?.database?.configured ? 'Connected' : 'Not Connected'}
                </span>
              </div>
            </div>

            {/* Notes */}
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Important Notes</p>
                  <ul className="text-xs text-amber-700 mt-1 space-y-1 list-disc list-inside">
                    <li>Changes to API keys take effect immediately</li>
                    <li>Test configurations after making changes</li>
                    <li>Database configuration can only be changed via environment variables</li>
                    <li>Keys stored in database override environment variables</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Login Security / Progressive Lockout — configurable defence
          ladder so support can tune the aggressiveness of brute-force
          protection without redeploying. Stored in admin_settings. */}
      <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`} data-testid="login-security-card">
        <div className="mb-4">
          <h2 className={`text-lg font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Lock className="w-5 h-5 text-violet-500" />
            Login security &amp; lockout policy
          </h2>
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            Progressive lockout: after the attempt limit is hit, each further lock escalates to the next stage. The final stage shows the custom &quot;contact customer service&quot; message.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Attempts before lockout</label>
            <Input
              type="number"
              min="1"
              max="20"
              value={backofficeSettings?.max_login_attempts ?? 3}
              onChange={(e) => setBackofficeSettings({ ...backofficeSettings, max_login_attempts: parseInt(e.target.value, 10) || 1 })}
              data-testid="login-security-max-attempts"
            />
            <p className="text-xs text-gray-500 mt-1">How many wrong passwords trigger a lockout.</p>
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Lockout stages (minutes, comma-separated)</label>
            <Input
              value={(backofficeSettings?.lockout_stages_minutes || [5, 15, 60, 1440]).join(", ")}
              onChange={(e) => {
                const parsed = e.target.value.split(",").map(s => parseInt(s.trim(), 10)).filter(n => !Number.isNaN(n) && n > 0);
                setBackofficeSettings({ ...backofficeSettings, lockout_stages_minutes: parsed });
              }}
              data-testid="login-security-stages"
              placeholder="5, 15, 60, 1440"
            />
            <p className="text-xs text-gray-500 mt-1">e.g. 5, 15, 60, 1440 → 5 min → 15 min → 1 h → 24 h.</p>
          </div>
        </div>
        <div className="mb-4">
          <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Final stage message</label>
          <Input
            value={backofficeSettings?.final_lockout_message || ""}
            onChange={(e) => setBackofficeSettings({ ...backofficeSettings, final_lockout_message: e.target.value })}
            data-testid="login-security-final-msg"
            placeholder="Your account has been locked for security. Please contact customer service to unlock it."
          />
        </div>
        <div className="flex justify-end">
          <Button
            data-testid="login-security-save"
            onClick={async () => {
              try {
                await axios.put(`${API}/backoffice/settings`, {
                  max_login_attempts: backofficeSettings?.max_login_attempts ?? 3,
                  lockout_stages_minutes: backofficeSettings?.lockout_stages_minutes || [5, 15, 60, 1440],
                  final_lockout_message: backofficeSettings?.final_lockout_message || "",
                }, { withCredentials: true });
                toast.success("Login security policy saved");
                fetchBackofficeSettings();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Failed to save");
              }
            }}
          >
            Save policy
          </Button>
        </div>
      </Card>

      {/* IP Whitelist Section */}
      <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className={`text-lg font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <Shield className="w-5 h-5 text-violet-500" />
              IP Whitelist
            </h2>
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Restrict admin panel access to specific IP addresses</p>
          </div>
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            backofficeSettings?.ip_whitelist_enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
          }`}>
            {backofficeSettings?.ip_whitelist_enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>

        {/* Add IP Form */}
        <div className="flex gap-3 mb-4">
          <Input
            placeholder="IP address (e.g., 192.168.1.1 or 192.168.1.0/24)"
            value={newIp.ip}
            onChange={(e) => setNewIp({...newIp, ip: e.target.value})}
            className="flex-1"
          />
          <Input
            placeholder="Description (optional)"
            value={newIp.description}
            onChange={(e) => setNewIp({...newIp, description: e.target.value})}
            className="flex-1"
          />
          <Button onClick={addIpToWhitelist}>Add IP</Button>
        </div>

        {/* IP List */}
        {loadingWhitelist ? (
          <div className="text-center py-4">
            <RefreshCw className="w-5 h-5 animate-spin mx-auto text-gray-400" />
          </div>
        ) : ipWhitelist.length === 0 ? (
          <div className={`text-center py-8 border-2 border-dashed rounded-lg ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
            <Globe className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
            <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No IPs in whitelist</p>
            <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>All IPs can access the admin panel</p>
          </div>
        ) : (
          <div className="space-y-2">
            {ipWhitelist.map((item) => (
              <div key={item.ip} className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                <div>
                  <p className={`font-mono ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.ip}</p>
                  {item.description && (
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{item.description}</p>
                  )}
                </div>
                <Button variant="ghost" size="sm" className="text-red-500" onClick={() => removeIpFromWhitelist(item.ip)}>
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* IP Blocklist Section */}
      <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className={`text-lg font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <Lock className="w-5 h-5 text-red-500" />
              Blocked IPs
            </h2>
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Block specific IP addresses from accessing the platform</p>
          </div>
          <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700">
            {blockedIps.length} Blocked
          </span>
        </div>

        {/* Add Blocked IP Form */}
        <div className="flex gap-3 mb-4">
          <Input
            placeholder="IP address to block"
            value={newBlockedIp.ip}
            onChange={(e) => setNewBlockedIp({...newBlockedIp, ip: e.target.value})}
            className="flex-1"
          />
          <Input
            placeholder="Reason (optional)"
            value={newBlockedIp.reason}
            onChange={(e) => setNewBlockedIp({...newBlockedIp, reason: e.target.value})}
            className="flex-1"
          />
          <Button onClick={addIpToBlocklist} variant="destructive">Block IP</Button>
        </div>

        {/* Blocked IP List */}
        {loadingWhitelist ? (
          <div className="text-center py-4">
            <RefreshCw className="w-5 h-5 animate-spin mx-auto text-gray-400" />
          </div>
        ) : blockedIps.length === 0 ? (
          <div className={`text-center py-8 border-2 border-dashed rounded-lg ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
            <Shield className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
            <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No blocked IPs</p>
            <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Block suspicious IPs to enhance security</p>
          </div>
        ) : (
          <div className="space-y-2">
            {blockedIps.map((item) => (
              <div key={item.ip} className={`flex items-center justify-between p-3 rounded-lg border border-red-200 ${isDark ? 'bg-red-900/20' : 'bg-red-50'}`}>
                <div>
                  <p className={`font-mono ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.ip}</p>
                  {item.reason && (
                    <p className={`text-xs ${isDark ? 'text-red-400' : 'text-red-600'}`}>Reason: {item.reason}</p>
                  )}
                  {item.blocked_at && (
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      Blocked: {new Date(item.blocked_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <Button variant="outline" size="sm" onClick={() => removeIpFromBlocklist(item.ip)}>
                  Unblock
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Dialogs */}
      <Dialog open={showStripeConfig} onOpenChange={setShowStripeConfig}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configure Stripe</DialogTitle>
            <DialogDescription>Update payment processing credentials</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveStripeConfig} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Secret API Key *</label>
              <Input
                type="password"
                value={stripeForm.api_key}
                onChange={(e) => setStripeForm({...stripeForm, api_key: e.target.value})}
                placeholder="sk_live_..."
                required
              />
              <p className="text-xs text-gray-500 mt-1">Found in Stripe Dashboard → Developers → API keys</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Publishable Key *</label>
              <Input
                value={stripeForm.publishable_key}
                onChange={(e) => setStripeForm({...stripeForm, publishable_key: e.target.value})}
                placeholder="pk_live_..."
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Webhook Secret (optional)</label>
              <Input
                type="password"
                value={stripeForm.webhook_secret}
                onChange={(e) => setStripeForm({...stripeForm, webhook_secret: e.target.value})}
                placeholder="whsec_..."
              />
            </div>
            <Button type="submit" className="w-full" disabled={savingConfig}>
              {savingConfig ? "Saving..." : "Save Stripe Configuration"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showEmailConfig} onOpenChange={setShowEmailConfig}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configure Email</DialogTitle>
            <DialogDescription>Update email service credentials</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveEmailConfig} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Provider</label>
              <Select value={emailForm.provider} onValueChange={(v) => setEmailForm({...emailForm, provider: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="brevo">Brevo (Sendinblue)</SelectItem>
                  <SelectItem value="sendgrid">SendGrid</SelectItem>
                  <SelectItem value="mailgun">Mailgun</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">API Key *</label>
              <Input
                type="password"
                value={emailForm.api_key}
                onChange={(e) => setEmailForm({...emailForm, api_key: e.target.value})}
                placeholder="Your API key"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Sender Email *</label>
              <Input
                type="email"
                value={emailForm.sender_email}
                onChange={(e) => setEmailForm({...emailForm, sender_email: e.target.value})}
                placeholder="noreply@yourdomain.com"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Sender Name</label>
              <Input
                value={emailForm.sender_name}
                onChange={(e) => setEmailForm({...emailForm, sender_name: e.target.value})}
                placeholder="SeatPilot"
              />
            </div>
            <Button type="submit" className="w-full" disabled={savingConfig}>
              {savingConfig ? "Saving..." : "Save Email Configuration"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showAiConfig} onOpenChange={setShowAiConfig}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configure AI</DialogTitle>
            <DialogDescription>Update AI chat credentials</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveAiConfig} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Provider</label>
              <Select value={aiForm.provider} onValueChange={(v) => setAiForm({...aiForm, provider: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="openai">OpenAI</SelectItem>
                  <SelectItem value="openai">OpenAI</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">API Key *</label>
              <Input
                type="password"
                value={aiForm.api_key}
                onChange={(e) => setAiForm({...aiForm, api_key: e.target.value})}
                placeholder="Your API key"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Model</label>
              <Select value={aiForm.model} onValueChange={(v) => setAiForm({...aiForm, model: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gpt-4o-mini">GPT-4o Mini</SelectItem>
                  <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                  <SelectItem value="gpt-4-turbo">GPT-4 Turbo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full" disabled={savingConfig}>
              {savingConfig ? "Saving..." : "Save AI Configuration"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
